import { useCallback, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDoctorPage } from './useDoctorPage';
import {
  tabFromParam, sectionFromParam, applyOrder, effectiveTheme, themeForTemplate, isReadOnly,
} from './MyPage.shared';

// ─────────────────────────────────────────────────────────────────────────────
// The «صفحهٔ من» screen container: data (useDoctorPage), the URL contract and
// every handler — ONCE, for both surfaces (DESIGN_CONCEPT §4.3: render files
// are render-only). The render layer passes `notify(kind, key)` so the hook
// never touches a toast itself.
//
// URL is state (§3.2): /my-page?tab=builder|leads&section=<kind>. On the
// phone `section` IS the full-screen editor, so platform back closes it.
// ─────────────────────────────────────────────────────────────────────────────

export function useMyPageScreen({ notify = () => {} } = {}) {
  const data = useDoctorPage();
  const { page, actions } = data;
  const [params, setParams] = useSearchParams();
  const tab = tabFromParam(params.get('tab'));
  const section = sectionFromParam(params.get('section'));
  const [publishOpen, setPublishOpen] = useState(false);
  // A preview link the browser refused to open in a new tab — offered as a link instead.
  const [blockedPreview, setBlockedPreview] = useState(null);
  // Whether THIS screen pushed the open section's history entry: then closing
  // pops it (so platform back and the header's back agree); a deep link that
  // arrived with ?section= is replaced instead, never popped out of the app.
  const pushedSection = useRef(false);

  const setParam = useCallback((key, value, { push = false } = {}) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value == null || value === '') next.delete(key); else next.set(key, value);
      return next;
    }, { replace: !push });
  }, [setParams]);

  const selectTab = useCallback((id) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id === 'builder') next.delete('tab'); else next.set('tab', id);
      next.delete('section');
      return next;
    }, { replace: true });
  }, [setParams]);

  const readOnly = isReadOnly(page);

  const changeTemplate = useCallback((templateId) =>
    actions.updatePage('template', { templateId, theme: themeForTemplate(templateId, page?.theme) }),
  [actions, page]);

  const changeTheme = useCallback((theme) =>
    actions.updatePage('theme', { theme: effectiveTheme(page?.templateId, theme) }),
  [actions, page]);

  const saveSlug = useCallback((slug) => actions.updatePage('slug', { slug }), [actions]);

  const toggleSection = useCallback((kind, enabled) =>
    actions.updateSection(`toggle-${kind}`, kind, { enabled }), [actions]);

  const moveSections = useCallback(async (kinds) => {
    const res = await actions.reorder(kinds);
    if (!res.ok) notify('error', 'myPage.sections.reorderFailed');
    return res;
  }, [actions, notify]);

  const preview = useCallback(async () => {
    setBlockedPreview(null);
    // Opened NOW, inside the click, so a popup blocker lets it through; it is
    // pointed at the signed link once the BFF has minted one.
    let win = null;
    try { win = window.open('', '_blank'); } catch { win = null; }
    const res = await actions.preview();
    if (!res.ok || !res.url) {
      try { win?.close(); } catch { /* already gone */ }
      return res;
    }
    if (win) {
      try { win.opener = null; } catch { /* cross-origin guard */ }
      win.location.href = res.url;
    } else {
      setBlockedPreview(res.url);
      notify('info', 'myPage.statusCard.popupBlocked');
    }
    return res;
  }, [actions, notify]);

  const publish = useCallback(async (complianceAccepted) => {
    const res = await actions.publish(complianceAccepted);
    if (res.ok && res.published) notify('success', 'myPage.publish.done');
    return res;
  }, [actions, notify]);

  const unpublish = useCallback(async () => {
    const res = await actions.unpublish();
    if (res.ok) notify('success', 'myPage.statusCard.unpublished');
    else notify('error', 'myPage.statusCard.unpublishFailed');
    return res.ok;
  }, [actions, notify]);

  return {
    ...data,
    tab,
    section,
    readOnly,
    selectTab,
    openSection: (kind) => { pushedSection.current = true; setParam('section', kind, { push: true }); },
    selectSection: (kind) => setParam('section', kind),
    closeSection: () => {
      if (pushedSection.current) { pushedSection.current = false; window.history.back(); } else setParam('section', null);
    },
    publishOpen,
    openPublish: () => setPublishOpen(true),
    closePublish: () => setPublishOpen(false),
    blockedPreview,
    changeTemplate,
    changeTheme,
    saveSlug,
    toggleSection,
    moveSections,
    preview,
    publish,
    unpublish,
    applyOrder,
  };
}

export default useMyPageScreen;
